import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";

const ROLES = [
    { value: "admin", label: "Administrator" },
    { value: "kundeservice", label: "Kundeservice" },
    { value: "medarbejder", label: "Medarbejder" },
];

// UI-only role variants (medarbejder is split into "med montør" / "uden montør")
const UI_ROLE_OPTIONS = [
    { key: "admin", label: "Administrator", role: "admin", has_installer: null },
    { key: "kundeservice", label: "Kundeservice", role: "kundeservice", has_installer: null },
    { key: "medarbejder_med", label: "Medarbejder med montør", role: "medarbejder", has_installer: true },
    { key: "medarbejder_uden", label: "Medarbejder uden montør", role: "medarbejder", has_installer: false },
];

const userToUiKey = (u) => {
    if (u.role !== "medarbejder") return u.role;
    if (u.has_installer === true) return "medarbejder_med";
    if (u.has_installer === false) return "medarbejder_uden";
    return "medarbejder_uden"; // legacy fallback: existing employees without flag default to "uden montør"
};

const installerLabel = (u) => {
    if (u.role !== "medarbejder") return "-";
    if (u.has_installer === true) return "Med montør";
    if (u.has_installer === false) return "Uden montør";
    return "Ikke angivet";
};

export default function Users() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!confirm("Slet bruger?")) return;
        try {
            await api.delete(`/users/${id}`);
            toast.success("Bruger slettet");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke slette");
        }
    };

    return (
        <div className="space-y-6" data-testid="users-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-medium text-[#2C2A29]">Brugere</h1>
                    <p className="text-sm text-[#78736E] mt-1">Administrer medarbejdere og adgange</p>
                </div>
                <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="add-user-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Ny bruger
                </Button>
            </div>

            <div className="bg-white border border-[#E5E1DA] rounded-xl overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#F4F2EF] hover:bg-[#F4F2EF] border-[#E5E1DA]">
                            <TableHead>Navn</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Rolle</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Handling</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#78736E]">Indlæser...</TableCell></TableRow>}
                        {users.map((u) => (
                            <TableRow key={u.id} className="hover:bg-[#FAFAF9] border-[#E5E1DA]" data-testid={`user-row-${u.id}`}>
                                <TableCell className="font-medium">{u.name}</TableCell>
                                <TableCell className="text-sm text-[#5C5854]">{u.email}</TableCell>
                                <TableCell>
                                    <Badge variant="secondary" className="bg-[#F0EBE1] text-[#4A4641] border-0">
                                        {ROLES.find((r) => r.value === u.role)?.label || u.role}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-[#5C5854]" data-testid={`user-type-${u.id}`}>
                                    {installerLabel(u)}
                                </TableCell>
                                <TableCell>
                                    {u.active ? <span className="text-xs text-[#15803D]">Aktiv</span> : <span className="text-xs text-[#78736E]">Inaktiv</span>}
                                </TableCell>
                                <TableCell className="text-right">
                                    <Button variant="ghost" size="sm" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => remove(u.id)} className="text-[#993C3C]" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <UserDialog open={open} setOpen={setOpen} user={editing} onSaved={load} />
        </div>
    );
}

function UserDialog({ open, setOpen, user, onSaved }) {
    const [form, setForm] = useState({ name: "", email: "", password: "", uiRole: "kundeservice" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user) setForm({ name: user.name, email: user.email, password: "", uiRole: userToUiKey(user) });
        else setForm({ name: "", email: "", password: "", uiRole: "kundeservice" });
    }, [user, open]);

    const save = async () => {
        if (!form.name || !form.email || (!user && !form.password)) {
            toast.error("Udfyld alle påkrævede felter");
            return;
        }
        const opt = UI_ROLE_OPTIONS.find((o) => o.key === form.uiRole) || UI_ROLE_OPTIONS[1];
        setSaving(true);
        try {
            if (user) {
                const payload = { name: form.name, email: form.email, role: opt.role, has_installer: opt.has_installer };
                if (form.password) payload.password = form.password;
                await api.patch(`/users/${user.id}`, payload);
            } else {
                await api.post("/users", {
                    name: form.name,
                    email: form.email,
                    password: form.password,
                    role: opt.role,
                    has_installer: opt.has_installer,
                });
            }
            toast.success("Gemt");
            setOpen(false);
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke gemme");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading">{user ? "Rediger bruger" : "Ny bruger"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div><Label>Navn *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                    <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                    <div>
                        <Label>{user ? "Ny adgangskode (valgfri)" : "Adgangskode *"}</Label>
                        <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
                    </div>
                    <div>
                        <Label>Rolle</Label>
                        <Select value={form.uiRole} onValueChange={(v) => setForm({ ...form, uiRole: v })}>
                            <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {UI_ROLE_OPTIONS.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`user-role-option-${r.key}`}>{r.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Annuller</Button>
                    <Button onClick={save} disabled={saving} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="save-user-button">Gem</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
